import { autoScrollViewHelper } from '../../global/modules/autoScrollVewHelper';
import { getAuthToken } from '../api/apiRequest';
import { logError } from '../../global/utils/logger';
import { actions } from '../context/actions';
import {
    STEP_NAMES,
    CCAConstant,
    ERROR_SCREEN,
    MANDATORY_BUSINESS_TYPE,
    APPLICATION_STATUS_PATTERN,
    POD_ERROR_SCREEN
} from '../constants';
import { STORAGE_CONFIG } from '../../../constants/storageConfig';
import { CCA_ENTRY_SCREEN } from '../api/apiConfigCca';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../constants/analyticsConstants/Ecommerce';
import config from '../../App/config';
import { PAYMENT_PREFERENCE_POD } from '../../guidedReg/constants';
import { authorizedUsersFormRules } from '../formValidation/validationRules';

export const getFormTitle = key =>
    key
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

export const runValidation = (
    values,
    validationRules,
    ccaDispatch,
    ccaAction,
    formStep,
    sendEventsForCcaStepFormError
) => {
    const errors = {};
    const ccaSubmissionId = sessionStorage?.getItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_SUBMISSION_ID);
    const getFormErrorIndex = key => (STEP_NAMES.indexOf(key) || 0) + 1;
    try {
        for (const field in validationRules) {
            if (validationRules.hasOwnProperty(field)) {
                const rules = validationRules[field];
                if (field === 'authorizedUsers') {
                    const errorData = validateAuthorizedUsers(values.authorizedUsers, authorizedUsersFormRules);
                    errors.errorAuthorizedUsers = errorData.authorizedUsersError;
                } else {
                    for (const rule of rules) {
                        if (rule.condition(values[field])) {
                            errors[field] = rule.message;
                            sendEventsForCcaStepFormError(
                                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_FORM_ERROR,
                                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_FORM_NAME,
                                rule.message,
                                field,
                                getFormTitle(formStep),
                                window.location.href,
                                getFormErrorIndex(formStep),
                                ccaSubmissionId
                            );
                            break;
                        }
                    }
                }
            }
        }
    } catch (error) {
        logError(error, false, 'ccaValidateAndSave', {});
    }
    const payload = {
        step: formStep,
        error: errors
    };
    ccaDispatch({ type: ccaAction.FORM_ERROR_DATA, payload });
    return errors;
};

export const validateAuthorizedUsers = (users, rules) => {
    const errors = users.map(user => {
        const userErrors = {};
        Object.entries(rules).forEach(([field, fieldRules]) => {
            const message = fieldRules.find(rule => rule.condition(user[field] || ''))?.message;
            if (message) {
                userErrors[field] = message;
            }
        });
        return Object.keys(userErrors).length > 0 ? userErrors : null;
    });
    return errors.some(Boolean) ? { authorizedUsersError: errors } : null;
};

export const validateAndSave = (
    values,
    validationRules,
    ccaDispatch,
    ccaAction,
    formStep,
    formData,
    sendEventsForCcaStepFormError,
) => {
    const errors = runValidation(
        values,
        validationRules,
        ccaDispatch,
        ccaAction,
        formStep,
        sendEventsForCcaStepFormError,
    );
    autoScrollViewHelper(errors);
    const isValid = Object.keys(errors).length === 0;
    if (formStep !== STEP_NAMES[CCAConstant.TWO] && isValid) {
        saveState(values, formStep, formData, ccaDispatch, ccaAction);
    }
    return isValid;
};

export const saveState = (state, formStep, formData, ccaDispatch, ccaAction) => {
    const payload = {
        step: formStep,
        data: {
            ...formData[formStep]?.data,
            ...state
        }
    };
    ccaDispatch({ type: ccaAction.SAVE_FORM_DATA, payload });
};

export const validateFederalTaxId = value => {
    // Remove any non-digit characters from the input
    const sanitizedValue = value.replace(CCAConstant.NUMBER_FORMAT, '');
    // Ensure the length of the sanitized value is no more than 9 digits
    if (value.length < 3) return sanitizedValue;
    return `${sanitizedValue.slice(0, CCAConstant.TWO)}-${sanitizedValue.slice(CCAConstant.TWO, CCAConstant.NINE)}`;
};

export const validateSalesIdChange = value => {
    const sanitizedValue = value.replace(CCAConstant.NUMBER_FORMAT, '');
    // Ensure the length of the sanitized value is no more than 6 digits
    return sanitizedValue.slice(0, CCAConstant.SIX);
};

export const validateGSTnumber = value => {
    // Remove any non-digit characters from the input
    const sanitizedValue = value.replace(CCAConstant.NUMBER_FORMAT, '');
    return sanitizedValue.slice(0, CCAConstant.NINE);
};

export const navigateToForms = (navigate, number) => {
    navigate(`/${STEP_NAMES[number]}`);
};

export const maskNumber = number => {
    // Split the input number by '-' if it contains a hyphen
    const parts = number?.split('-');
    let maskedNumber = '';
    // Iterate through the parts and mask each part
    for (const part of parts) {
        const maskedPart = 'X'.repeat(part.length);
        maskedNumber += maskedPart;
        // Add a hyphen if there are more parts to come
        if (parts.indexOf(part) < parts.length - 1) {
            maskedNumber += '-';
        }
    }
    return maskedNumber;
};

export const clearInlineError = (key, formData, formStep, type, dispatch) => {
    const payload = {
        step: formStep,
        error: {
            ...formData[formStep]?.error,
            [key]: ''
        }
    };
    dispatch({ type, payload });
};

export const clearErrorsByIndex = (formStep, ccaDispatch) => {
    const payload = {
        step: STEP_NAMES[formStep],
        error: {}
    };
    ccaDispatch({ type: actions.FORM_ERROR_DATA, payload });
};

export const validatePostalCodeChange = value => {
    const sanitizedValue = value.replace(CCAConstant.NUMBER_ALPHABET_FORMAT, '');
    // Ensure the length of the sanitized value is no more than 9 digits
    if (value.length < CCAConstant.FOUR) return sanitizedValue;
    return `${sanitizedValue.slice(0, CCAConstant.THREE)} ${sanitizedValue.slice(CCAConstant.THREE, CCAConstant.SIX)}`;
};

export const validateZipCodeChange = value => {
    // Remove any non-digit characters from the input
    const sanitizedValue = value.replace(CCAConstant.NUMBER_FORMAT, '');
    // Ensure the length of the sanitized value is no more than 9 digits
    if (value.length < CCAConstant.SIX) return sanitizedValue;
    return `${sanitizedValue.slice(0, CCAConstant.FIVE)}-${sanitizedValue.slice(CCAConstant.FIVE, CCAConstant.NINE)}`;
};

export const addFormReloadWarning = isFormDirty => {
    window.onbeforeunload = () => {
        if (isFormDirty) {
            return 'You have unsaved changes. Are you sure you want to leave this page?';
        }
    };

    return () => {
        window.onbeforeunload = null;
    };
};

export const saveVerifyAddressState = async (state, formData, ccaDispatch, ccaAction, setStateUpdated) => {
    const saveFormData = async payload => {
        await ccaDispatch({
            type: ccaAction.SAVE_FORM_DATA,
            payload
        });
    };

    if (state?.noBusinessMatchOnExperian) {
        const payloadForReviewApplication = {
            step: STEP_NAMES[CCAConstant.ONE],
            data: {
                ...formData[STEP_NAMES[CCAConstant.ONE]]?.data,
                noBusinessMatchOnExperian: state?.noBusinessMatchOnExperian
            }
        };
        await saveFormData(payloadForReviewApplication);
        setStateUpdated(true);
        return;
    }

    const businessName = state?.businessName;
    await saveFormData({
        step: STEP_NAMES[CCAConstant.ONE],
        data: {
            ...formData[STEP_NAMES[CCAConstant.ONE]]?.data,
            businessName,
            noBusinessMatchOnExperian: state?.noBusinessMatchOnExperian
        }
    });

    const businessAddress = state?.address;
    const isBillingAddressSame = formData[STEP_NAMES[CCAConstant.TWO]]?.data?.isAddressSameCheckbox;
    const payloadForBusinessAndBilling = {
        step: STEP_NAMES[CCAConstant.TWO],
        data: {
            ...formData[STEP_NAMES[CCAConstant.TWO]]?.data,
            address: businessAddress?.street,
            city: businessAddress?.city,
            state: businessAddress?.state,
            zipCode: businessAddress?.zip
        }
    };

    if (isBillingAddressSame) {
        payloadForBusinessAndBilling.data = {
            ...payloadForBusinessAndBilling.data,
            billingAddress: businessAddress?.street,
            billingCity: businessAddress?.city,
            billingState: businessAddress?.state,
            billingZipCode: businessAddress?.zip
        };
    }
    await saveFormData(payloadForBusinessAndBilling);
    setStateUpdated(true);
};

export const refreshTokenForSubmissionApiCall = async () => {
    try {
        const { data: { access_token: token = '' } = {}, error: authError = {} } = (await getAuthToken()) || {};
        if (token) {
            sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.GR_TOKEN, token);
        } else {
            throw new Error(authError);
        }
    } catch (error) {
        logCCAError(error);
    }
};

export const checkIfSubmissionIdExists = () => {
    const submissonId = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_SUBMISSION_ID);

    if (isPreviewMode() || isAuthorMode()) {
        return;
    }

    if (!submissonId) {
        window.location.href = CCA_ENTRY_SCREEN;
    }
};

export const clearSession = () => {
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_SUBMISSION_ID);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CLIENT_IP_ADDRESS);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.GR_TOKEN);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.SALESREPID);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.SFID);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.SESSIONID);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.USER_EXISTS);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.DATA_TO_FINALIZE_CALL);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.ADDRESS_SELECTED);
};

export const isPODFlow = () => {
    const paymentPreferenceType = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.PAYMENT_PREFERENCE_TYPE);
    return paymentPreferenceType === PAYMENT_PREFERENCE_POD;
};

export const logCCAError = error => {
    sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_GLOBAL_ERROR, error);
    logError(error);
    window.location.href = isPODFlow() ? POD_ERROR_SCREEN : ERROR_SCREEN;
};

export const getShortFieldNameForReviewApplication = (label, groupLabel) =>
    Object.keys(groupLabel).find(key => groupLabel[key] === label);

export const isCoversionFlow = () => {
    const userExists = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.USER_EXISTS);
    return userExists == 'true';
};

export const generateRandomPassword = () => {
    try {
        const upperCaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lowerCaseLetters = 'abcdefghijklmnopqrstuvwxyz';
        const digits = '0123456789';
        const allCharacters = upperCaseLetters + lowerCaseLetters + digits;
        const getRandomChar = characters => characters[Math.floor(Math.random() * characters.length)];
        // Ensure at least one uppercase letter, one lowercase letter, and one digit
        let password = [getRandomChar(upperCaseLetters), getRandomChar(lowerCaseLetters), getRandomChar(digits)];
        // Fill the rest of the password length to make it 8 characters
        for (let i = 3; i < 8; i++) {
            password.push(getRandomChar(allCharacters));
        }
        password = password.sort(() => Math.random() - 0.5);
        return password.join('');
    } catch (error) {
        logCCAError(error);
    }
};

export const isPreviewMode = () => {
    if (config.pagePaths && config.pagePaths.isPreviewMode) {
        return true;
    }

    return false;
}

export const isAuthorMode = () => {
    if (config.pagePaths && config.pagePaths.isAuthorMode) {
        return true;
    }

    return false;
}

export const doingBusinessAsLabel = (tradeNameLabel = '', businessTypeValue) => {
    if (!MANDATORY_BUSINESS_TYPE.includes(businessTypeValue)) {
        return `${tradeNameLabel} (optional)`;
    }
    return tradeNameLabel;
};

export const authorizedUsersPayload = (users = []) => {
    const MAX_USERS = 4;
    const formattedUsers = Array.from({ length: MAX_USERS }).reduce((acc, _, index) => {
        const user = users[index] || {};
        const i = index + 1;
        acc[`authorizedFirstName${i}`] = user.firstName || '';
        acc[`authorizedLastName${i}`] = user.lastName || '';
        return acc;
    }, {});
    return formattedUsers;
};

export const validateHeaderBackExitHelpVisible = location => {
    const pathname = location?.pathname && location.pathname !== '/' ? location.pathname : window.location.pathname;
    return APPLICATION_STATUS_PATTERN.test(pathname);
};
